import React, { useCallback, useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Layout, Modal, Typography, Table } from 'antd';
import { apiClient, apiErrorMessage } from '../../utils';
import {
  QaSubError,
  User,
  Equipment,
  Country,
  City,
  Division,
  Section,
  Cell,
  ErrorFinder,
  Supplier,
  FourM,
} from '../../models/index';
import { QaSubErrorEditPage } from './qa-sub-error-edit-page';
import moment from 'moment';

interface IQaSubErrorsPage {
  errorId?: string;
  canEdit: boolean;
  onRefresh: () => void;
}
const { Paragraph } = Typography;
const months = [
  { index: 1, name: 'january', description: "Ocak" },
  { index: 2, name: 'february', description: "Şubat" },
  { index: 3, name: 'march', description: "Mart" },
  { index: 4, name: 'april', description: "Nisan" },
  { index: 5, name: 'may', description: "Mayıs" },
  { index: 6, name: 'june', description: "Haziran" },
  { index: 7, name: 'july', description: "Temmuz" },
  { index: 8, name: 'august', description: "Ağustos" },
  { index: 9, name: 'september', description: "Eylül" },
  { index: 10, name: 'october', description: "Ekim" },
  { index: 11, name: 'november', description: "Kasım" },
  { index: 12, name: 'december', description: "Aralık" }];

export function QaSubErrorsPage(props: IQaSubErrorsPage) {
  const { Content } = Layout;
  const [loading, setLoading] = useState(false);
  const [qaSubErrors, setQaSubErrors] = useState<(QaSubError & { key: string })[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);

  const loadQaSubErrors = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`qa-sub-errors/${props?.errorId}`)
      .then((response: any) => {
        const responseQaSubError: QaSubError[] = response.data;
        const responseData = responseQaSubError.map(qaSubError => {
          return {
            key: qaSubError.id,
            ...qaSubError,
          };
        });
        setQaSubErrors(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadQaSubErrors();
  }, [loadQaSubErrors]);


  const openEditModal = () => {
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  const onQasubErrorEditSaved = () => {
    closeEditModal();
    loadQaSubErrors();
  };

  const tableMainColumns: any[] = [
    {
      title: 'Hata no',
      dataIndex: 'errorNo',
      key: 'errorNo',
    }, {
      title: 'Problem durumu',
      dataIndex: 'problemStatus',
      key: 'problemStatus',
    }, {
      title: 'Açık bekleyen gün sayısı',
      dataIndex: 'numberOfOpenWaitingDays',
      key: 'numberOfOpenWaitingDays',
    }, {
      title: 'Bildirim tarihi',
      dataIndex: 'firstNotificationDate',
      key: 'firstNotificationDate',
      render: (firstNotificationDate: string) => (moment(firstNotificationDate).format('DD.MM.yyyy')),
    }, {
      title: 'Hata kaydını açan',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: User) => createdBy?.fullName,
    }, {
      title: 'Hatayı tespit eden',
      dataIndex: 'errorFinder',
      key: 'errorFinder',
      render: (errorFinder: ErrorFinder) => errorFinder?.name,
    }, {
      title: 'İlk bulgu',
      dataIndex: 'firstFinding',
      key: 'firstFinding',
    }, {
      title: 'Malzeme no',
      dataIndex: 'equipment',
      key: 'equipment',
      render: (equipment: Equipment) => equipment?.code,
    }, {
      title: 'Malzeme tanımı',
      dataIndex: 'equipment',
      key: 'equipment',
      render: (equipment: Equipment) => equipment?.name,
    }, {
      title: 'Maliyet',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: string) => cost?.toString().concat("€"),
    }, {
      title: 'Ülke',
      dataIndex: 'country',
      key: 'country',
      render: (country: Country) => country?.name,
    }, {
      title: 'Şehir',
      dataIndex: 'city',
      key: 'city',
      render: (city: City) => city?.name,
    }, {
      title: 'Bölüm',
      dataIndex: 'division',
      key: 'division',
      render: (division: Division) => division?.name,
    }, {
      title: 'Kısım',
      dataIndex: 'section',
      key: 'section',
      render: (section: Section) => section?.name,
    }, {
      title: 'Hücre',
      dataIndex: 'cell',
      key: 'cell',
      render: (cell: Cell) => cell?.name,
    }, {
      title: 'Kişi',
      dataIndex: 'user',
      key: 'user',
      render: (user: User) => user?.fullName,
    }, {
      title: 'Tedarikçi',
      dataIndex: 'supplier',
      key: 'supplier',
      render: (supplier: Supplier) => supplier?.name,
    }, {
      title: 'Maliyet puanı',
      dataIndex: 'costScore',
      key: 'costScore',
    }, {
      title: 'Frekans',
      dataIndex: 'frequency',
      key: 'frequency',
    }, {
      title: 'Müşteri sınıfı',
      dataIndex: 'customerClass',
      key: 'customerClass',
    }, {
      title: 'Tespit yeri',
      dataIndex: 'detectionPlace',
      key: 'detectionPlace',
    }, {
      title: 'Ağırlık puanı',
      dataIndex: 'weightPoint',
      key: 'weightPoint',
    }, {
      title: '4M',
      dataIndex: 'fourM',
      key: 'fourM',
      render: (fourM: FourM) => fourM.name,
    },
  ];

  const getFrequenciesColumns = () => {
    const columns: any[] = [];
    months.forEach((row: any) => {
      columns.push({
        title: row.description,
        dataIndex: row.name,
        key: row.name,
        render: (value: string, records: any) => FrequencyDropDown(row.name, value, records.id),
      })
    })
    return columns;
  }

  const FrequencyDropDown = (field: string, value: string, qaSuberrorId: string) => {
    return (
      <Paragraph
        style={{ width: "100px" }}
        editable={{
          tooltip: false,
          onChange: (e) => onChangeFrequencyDropDown(field, e, qaSuberrorId),
          triggerType: ['icon'],
        }}
      >
        {value}
      </Paragraph>
    )
  };

  const tableColumns = tableMainColumns.concat(getFrequenciesColumns());

  const onChangeFrequencyDropDown = (name: string, value: string, qaSuberrorId: string) => {
    if (value === "") return;
    const monthIndex = months.filter((x => x.name === name))[0].index;
    setLoading(true);
    apiClient
      .put(`/qa-sub-errors/${qaSuberrorId}/qa-sub-error-frequency`, { month: monthIndex, numberOfViews: value })
      .then((response: any) => {
        loadQaSubErrors();
        props.onRefresh();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (

    <>
      <Content>
        <Table columns={tableColumns} dataSource={qaSubErrors} loading={loading} pagination={false} />
        {props.canEdit && (
          <Button key="add" icon={<PlusOutlined />} onClick={openEditModal} type="primary">
            Yeni alt hata kaydı ekle
          </Button>
        )}
      </Content>
      <Modal title="Alt hata kaydı" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <QaSubErrorEditPage errorId={props.errorId} onSaved={onQasubErrorEditSaved} onCancel={closeEditModal} />
      </Modal>
    </>
  );
}
